import React, { useState } from 'react';
import yaml from 'js-yaml'; // You'll need to install this package: npm install js-yaml @types/js-yaml

// Define types for our component
interface XmlToYamlProps {}

// Function to convert XML string to a JavaScript object
const xmlToObject = (xml: string): any => {
  try {
    // Create a DOM parser and parse the XML string
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    // Get the root element
    const rootElement = xmlDoc.documentElement;
    
    // Function to convert an XML node to a JavaScript object
    const nodeToObject = (node: Element): any => {
      const result: any = {};
      const childNodes = node.childNodes;
      
      // Process attributes if any
      if (node.attributes && node.attributes.length > 0) {
        result['@attributes'] = {};
        for (let i = 0; i < node.attributes.length; i++) {
          const attr = node.attributes[i];
          result['@attributes'][attr.nodeName] = attr.nodeValue;
        }
      }
      
      // Check if the node has only text content
      if (childNodes.length === 1 && childNodes[0].nodeType === Node.TEXT_NODE) {
        const text = childNodes[0].nodeValue?.trim();
        if (text) {
          return text;
        } else {
          return result;
        }
      }
      
      // Process child elements
      let hasElements = false;
      for (let i = 0; i < childNodes.length; i++) {
        const child = childNodes[i];
        
        // Skip non-element nodes like text nodes (whitespace)
        if (child.nodeType !== Node.ELEMENT_NODE) continue;
        
        hasElements = true;
        const childElement = child as Element;
        const childName = childElement.nodeName;
        const childObj = nodeToObject(childElement);
        
        // Handle multiple elements with the same name
        if (result[childName]) {
          // If it's already an array, push to it
          if (Array.isArray(result[childName])) {
            result[childName].push(childObj);
          } else {
            // Convert to array with existing and new values
            result[childName] = [result[childName], childObj];
          }
        } else {
          // First occurrence of this element name
          result[childName] = childObj;
        }
      }
      
      // If there are no elements and no attributes, check for text content
      if (!hasElements && Object.keys(result).length === 0) {
        const text = node.textContent?.trim();
        if (text) {
          return text;
        }
      }
      
      return result;
    };
    
    // Create result object with the root element name as the key
    const rootName = rootElement.nodeName;
    return { [rootName]: nodeToObject(rootElement) };
  } catch (error) {
    throw error;
  }
};

// Convert XML to YAML string
const convertXmlToYaml = (xml: string, indentSize: number = 2): string => {
  try {
    // First convert XML to a JavaScript object
    const jsObject = xmlToObject(xml);
    
    // Then convert the object to YAML
    const yamlString = yaml.dump(jsObject, {
      indent: indentSize,
      lineWidth: -1, // No line wrapping
      noRefs: true,  // Don't use YAML references
    });
    
    return yamlString;
  } catch (error) {
    throw error;
  }
};

// Generate XML document from template
const generateXmlFromTemplate = (template: string): string => {
  try {
    // Parse template parameters
    const rootElement = template === 'custom' ? 'root' : template;
    
    // Create a simple XML structure based on the template
    let xmlDoc = '';
    
    switch (template) {
      case 'book-catalog':
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<catalog>
  <book id="bk101">
    <author>Gambardella, Matthew</author>
    <title>XML Developer's Guide</title>
    <genre>Computer</genre>
    <price>44.95</price>
    <publish_date>2000-10-01</publish_date>
    <description>An in-depth look at creating applications with XML.</description>
  </book>
  <book id="bk102">
    <author>Ralls, Kim</author>
    <title>Midnight Rain</title>
    <genre>Fantasy</genre>
    <price>5.95</price>
    <publish_date>2000-12-16</publish_date>
    <description>A former architect battles corporate zombies.</description>
  </book>
</catalog>`;
        break;
        
      case 'api-config':
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<api-configuration>
  <base-url>https://api.example.com/v2</base-url>
  <timeout>30</timeout>
  <endpoints>
    <endpoint name="users">
      <path>/users</path>
      <methods>
        <method>GET</method>
        <method>POST</method>
        <method>PUT</method>
      </methods>
      <requires-auth>true</requires-auth>
    </endpoint>
    <endpoint name="products">
      <path>/products</path>
      <methods>
        <method>GET</method>
      </methods>
      <requires-auth>false</requires-auth>
    </endpoint>
  </endpoints>
  <authentication>
    <type>bearer-token</type>
    <token-endpoint>/auth/token</token-endpoint>
    <expires>3600</expires>
  </authentication>
</api-configuration>`;
        break;
        
      case 'website-config':
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<website>
  <settings>
    <title>My Website</title>
    <description>A personal website</description>
    <base_url>https://mywebsite.com</base_url>
    <theme>dark</theme>
    <language>en</language>
  </settings>
  <navigation>
    <item>
      <name>Home</name>
      <url>/</url>
      <order>1</order>
    </item>
    <item>
      <name>About</name>
      <url>/about</url>
      <order>2</order>
    </item>
    <item>
      <name>Contact</name>
      <url>/contact</url>
      <order>3</order>
    </item>
  </navigation>
  <footer>
    <copyright>© 2023 My Website</copyright>
    <social>
      <platform name="twitter">https://twitter.com/myhandle</platform>
      <platform name="github">https://github.com/myusername</platform>
    </social>
  </footer>
</website>`;
        break;
        
      case 'address-book':
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<address-book>
  <contact id="c1">
    <name>John Doe</name>
    <email>john.doe@example.com</email>
    <phone type="home">555-1234</phone>
    <phone type="mobile">555-5678</phone>
    <address>
      <street>123 Main St</street>
      <city>Anytown</city>
      <state>CA</state>
      <zip>12345</zip>
    </address>
  </contact>
  <contact id="c2">
    <name>Jane Smith</name>
    <email>jane.smith@example.com</email>
    <phone type="home">555-8765</phone>
    <phone type="mobile">555-4321</phone>
    <address>
      <street>456 Oak Ave</street>
      <city>Somewhere</city>
      <state>NY</state>
      <zip>67890</zip>
    </address>
  </contact>
</address-book>`;
        break;
        
      default:
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<${rootElement}>
  <!-- Add your XML content here -->
  <element attribute="value">
    <child>Text content</child>
    <child>More content</child>
  </element>
  <another-element>
    <nested>
      <deeply>Very nested content</deeply>
    </nested>
  </another-element>
</${rootElement}>`;
    }
    
    return xmlDoc;
  } catch (error) {
    throw error;
  }
};

// Format/Beautify XML
const formatXml = (xmlStr: string): string => {
  try {
    if (!xmlStr.trim()) {
      throw new Error('Please enter XML data to format');
    }
    
    // Parse the XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlStr, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    // Serialize back to string with pretty formatting
    const serializer = new XMLSerializer();
    let formattedXml = '';
    
    // Function to pretty print XML with indentation
    const prettyPrintXml = (node: Node, indent: string = ''): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue?.trim();
        return text ? indent + text + '\n' : '';
      }
      
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        let result = indent + '<' + element.nodeName;
        
        // Add attributes
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          result += ` ${attr.nodeName}="${attr.nodeValue}"`;
        }
        
        // Check if there are any child nodes
        if (element.childNodes.length === 0) {
          result += '/>\n';
          return result;
        }
        
        result += '>\n';
        
        // Process child nodes
        for (let i = 0; i < element.childNodes.length; i++) {
          result += prettyPrintXml(element.childNodes[i], indent + '  ');
        }
        
        result += indent + '</' + element.nodeName + '>\n';
        return result;
      }
      
      if (node.nodeType === Node.COMMENT_NODE) {
        return indent + '<!-- ' + node.nodeValue + ' -->\n';
      }
      
      return '';
    };
    
    // Add XML declaration
    formattedXml += '<?xml version="1.0" encoding="UTF-8"?>\n';
    
    // Format the document element
    formattedXml += prettyPrintXml(xmlDoc.documentElement);
    
    return formattedXml;
  } catch (error) {
    throw error;
  }
};

// Main component to convert XML to YAML
const XmlToYaml: React.FC<XmlToYamlProps> = () => {
  // State for XML input
  const [xmlInput, setXmlInput] = useState<string>('');
  // State for YAML output
  const [yamlOutput, setYamlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for file name
  const [fileName, setFileName] = useState<string>('xml_converted');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State to track if conversion happened
  const [isYamlGenerated, setIsYamlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for indent size
  const [indentSize, setIndentSize] = useState<number>(2);
  // State for template selection
  const [template, setTemplate] = useState<string>('custom');
  // State for conversion options
  const [preserveAttributes, setPreserveAttributes] = useState<boolean>(true);

  // Handle XML input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setXmlInput(e.target.value);
    setError('');
    setIsYamlGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use template
  const useTemplate = () => {
    try {
      const xmlTemplate = generateXmlFromTemplate(template);
      setXmlInput(xmlTemplate);
      setError('');
      setIsYamlGenerated(false);
    } catch (e) {
      setError(`Error generating template: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Format XML
  const handleFormatXml = () => {
    try {
      const formattedXml = formatXml(xmlInput);
      setXmlInput(formattedXml);
      setError('');
    } catch (e) {
      setError(`Error formatting XML: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert XML to YAML
  const handleConvertXmlToYaml = () => {
    setError('');
    setIsLoading(true);
    setIsYamlGenerated(false);
    setYamlOutput('');
    
    if (!xmlInput.trim()) {
      setError('Please enter XML data');
      setIsLoading(false);
      return;
    }

    try {
      const yamlString = convertXmlToYaml(xmlInput, indentSize);
      setYamlOutput(yamlString);
      setIsYamlGenerated(true);
    } catch (e) {
      setError(`Error converting XML to YAML: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setXmlInput('');
    setYamlOutput('');
    setError('');
    setFileName('xml_converted');
    setIsYamlGenerated(false);
  };

  // Copy YAML to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(yamlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download YAML file
  const downloadYaml = () => {
    const blob = new Blob([yamlOutput], { type: 'text/yaml;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'xml_converted'}.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">XML to YAML Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert XML data to YAML format. This tool transforms XML structure into YAML,
          maintaining the hierarchy and relationships between elements.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="xmlInput" className="font-medium text-gray-700">
                XML Input
              </label>
              <div className="flex space-x-2">
                <select
                  className="text-sm border border-gray-300 rounded p-1"
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                >
                  <option value="custom">Custom</option>
                  <option value="book-catalog">Book Catalog</option>
                  <option value="api-config">API Configuration</option>
                  <option value="website-config">Website Config</option>
                  <option value="address-book">Address Book</option>
                </select>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useTemplate}
                >
                  Use Template
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={handleFormatXml}
                >
                  Format XML
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setXmlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="xmlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={xmlInput}
              onChange={handleInputChange}
              placeholder="Paste your XML data here, e.g.,
<?xml version='1.0' encoding='UTF-8'?>
<library>
  <book id='1'>
    <title>Book Title</title>
    <author>Author Name</author>
    <year>2023</year>
  </book>
  <book id='2'>
    <title>Another Book</title>
    <author>Another Author</author>
    <year>2024</year>
  </book>
</library>"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                  File Name
                </label>
                <input
                  type="text"
                  id="fileName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={fileName}
                  onChange={handleFileNameChange}
                  placeholder="Enter file name (without extension)"
                />
              </div>
              
              <div>
                <label htmlFor="indentSize" className="block text-sm font-medium text-gray-700 mb-1">
                  Indent Size
                </label>
                <select
                  id="indentSize"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={indentSize}
                  onChange={(e) => setIndentSize(parseInt(e.target.value))}
                >
                  <option value="2">2 spaces</option>
                  <option value="4">4 spaces</option>
                  <option value="8">8 spaces</option>
                </select>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={preserveAttributes}
                  onChange={(e) => setPreserveAttributes(e.target.checked)}
                />
                Preserve XML Attributes as @attributes
              </label>
            </div>
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={handleConvertXmlToYaml}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to YAML'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              YAML Output
            </label>
            
            {isYamlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy YAML'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadYaml}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isYamlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    YAML output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isYamlGenerated && (
              <textarea
                id="yamlOutput"
                className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                value={yamlOutput}
                readOnly
              />
            )}
          </div>
          
          {isYamlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">XML Successfully Converted to YAML!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated YAML.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About XML to YAML Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms XML documents into YAML format by parsing the XML structure into a JavaScript object 
              and then serializing that object to YAML. The conversion preserves the hierarchical structure of your XML data
              while transforming it into the more human-readable YAML format.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Preserves XML Structure</strong> - Maintains the hierarchical relationships between XML elements</li>
              <li><strong>Handles XML Attributes</strong> - Option to preserve XML attributes as @attributes in YAML</li>
              <li><strong>Customizable Indentation</strong> - Adjust YAML spacing for better readability</li>
              <li><strong>Template Selection</strong> - Start with common XML structures for quick testing</li>
              <li><strong>XML Formatting</strong> - Beautify XML input for easier editing</li>
              <li><strong>Error Handling</strong> - Clear error messages for invalid XML input</li>
              <li><strong>One-Click Download</strong> - Save the converted YAML with a single click</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">XML to YAML Conversion</h3>
            <p className="text-gray-600">
              The conversion process follows these steps:
            </p>
            <ol className="list-decimal pl-5 text-gray-600 mt-2">
              <li>Parse the XML document into a DOM structure</li>
              <li>Recursively traverse the DOM tree to build a JavaScript object</li>
              <li>Handle special cases like attributes, text nodes, and repeated elements</li>
              <li>Serialize the JavaScript object to YAML using the js-yaml library</li>
              <li>Apply formatting according to user preferences (indentation)</li>
            </ol>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format XML" button to check if your XML is valid before conversion</li>
              <li>For XML with attributes, keep the "Preserve XML Attributes" option checked</li>
              <li>Start with a template to understand the converter's capabilities</li>
              <li>For complex XML structures, a larger indent size (4 or 8) may improve readability</li>
              <li>Use descriptive file names for downloaded YAML files that indicate their content</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Use Cases</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Configuration Conversion</strong> - Convert XML-based configs to YAML for modern applications</li>
              <li><strong>Data Migration</strong> - Transform legacy XML data into YAML for newer systems</li>
              <li><strong>API Response Processing</strong> - Convert XML API responses to the more readable YAML format</li>
              <li><strong>Documentation</strong> - Create YAML examples from XML for technical documentation</li>
              <li><strong>DevOps Automation</strong> - Convert between formats for CI/CD configuration files</li>
              <li><strong>Data Analysis</strong> - Transform XML datasets to YAML for easier parsing in data science workflows</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Key Differences Between XML and YAML</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Syntax</strong> - YAML uses indentation for hierarchy, while XML uses opening and closing tags</li>
              <li><strong>Verbosity</strong> - YAML is typically more concise than equivalent XML</li>
              <li><strong>Readability</strong> - YAML is designed to be more human-readable than XML</li>
              <li><strong>Comments</strong> - YAML supports native comments with the # symbol</li>
              <li><strong>Data Types</strong> - YAML has built-in support for more data types (booleans, nulls, etc.)</li>
              <li><strong>Attributes</strong> - XML has tag attributes; YAML doesn't have a direct equivalent</li>
              <li><strong>Namespaces</strong> - XML supports namespaces; YAML does not have native namespace support</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Limitations</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>XML namespaces may not be preserved in a way that could be converted back to XML</li>
              <li>XML processing instructions and some DOCTYPE declarations may be lost</li>
              <li>Complex XML schemas with specialized element types may not convert perfectly</li>
              <li>XML mixed content models (where elements contain both text and child elements) might not be represented intuitively in YAML</li>
              <li>Very large XML files may be slow to process in browser environments</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XmlToYaml;